/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/common-edit-logic';
import { InteractionEvent } from '@tencent/alloy-renderer';
import { reaction } from 'mobx';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import type { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import type { Result, SearchResultItem } from '@src/slide/application/service/search-replace-manager/search-replace-manager-v3.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { FORMAT_BRUSH_TEXT } from '@src/slide/application/service/format-brush/core/constant';
import { getBrushCursorStyle } from '@src/slide/application/service/format-brush/core/util';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';

/*
 * 这是给有textEditor的element用的 logic抽象类，内置了textEditor的一些通用业务逻辑处理，和必须实现的抽象方法
 *   基于 Common，加入额外逻辑的这种行为，相比继承，其实更像 mixin 的概念。
 *   如果结合logic的LifeHook，任何logic都可以实现 "logic插件"的mixin行为，是可插拔的。
 *   后面可以跟着logic重构重新设计一下
 */
export abstract class CommonEditLogicWithTextEditor extends CommonEditLogic {
    // cursor初始值要计算得到，因为可能存在"重复使用格式刷状态"时创建文本框，新创建的文本框要应用格式刷cursor
    public editorInitialCursor = this.getComputedCursor();

    public constructor(factoryOptions: InterfaceFactoryOptions<IElementBaseModel>, component: ComponentFactory) {
        super(factoryOptions, component);

        this.bindLogicHandlers();

        this.observeFormatBrushState();

        // 初始化的logic的时候，统一处理下文本高亮。因为如果高亮跳转的时候，model没加载，就不会有高亮
        // 这里在初始化的时候主动获取下
        this.handleUnHighlightSearchResult();
    }

    // handleEditorMouseDown 和 handleEditorMouseUp 子类要可访问，所以是public
    public handleEditorMouseDown(e: InteractionEvent): void {
        // 目前没有阻止冒泡，还是会触发component.mouseUp。这里加个标识，当标识为true时，阻止wrapper.handleMouseUp里应用格式刷的逻辑
        this.hasEditorRegisterDocumentMouseUp = true;
    }

    public handleEditorMouseUp(): void {
        this.hasEditorRegisterDocumentMouseUp = false;

        const formatBrushManager = (this.getAppService() as IPcEditorAppServices)
            .editableServiceLayer?.formatBrushManager;
        // editor的mouseUp 兼容了鼠标移出element，所以"格式刷刷文本"的场景，在这里触发应用逻辑，而不触发wrapper.handleMouseUp里的应用
        formatBrushManager?.onElementSelectionMouseUp();
    }

    protected getComputedCursor() {
        // 格式刷状态
        const formatBrushState = (this.getAppService() as IPcEditorAppServices)
            .editableServiceLayer?.formatBrushManager?.formatBrushState;
        const animBrushState = (this.getAppService() as IPcEditorAppServices)
            .editableServiceLayer?.animBrushManager?.animBrushState;
        if (animBrushState) {
            // Office 中动画刷动画来源元素鼠标指针不变
            const { model } = this.factoryOptions;
            const { uniqueID } = model || {};
            return uniqueID === animBrushState.originElId || animBrushState.allMediaEffect ?  'text' : getBrushCursorStyle(false);
        } if (formatBrushState?.data) {
            return getBrushCursorStyle(formatBrushState?.data === FORMAT_BRUSH_TEXT);
        }

        return 'text';
    }
    /*
     * 需要有bind场景的原因：
     *   1. 子类重写handler方法，同时希望引用父类的原逻辑作为基础逻辑来扩展。需要通过super.xx来引用，这种handler需要在父级原型上
     *   2. 在原型链上的方法，就没办法通过赋值来声明函数，如 handler = () => {}，也就没办法通过箭头函数的方式来避免this的指向问题
     *   3. 所以只能通过bind来绑定this，让子类中的handler的执行不出问题
     *
     * 因为之前的logic方法，没有需要子类重写扩展的场景，所以就没上述的问题，直接通过 `handler = () => {}`即可
     * 如果这里不bind，子类在传递的时候就需要手动去处理this问题。所以这里在源头直接bind
     */
    private bindLogicHandlers() {
        this.handleEditorMouseDown = this.handleEditorMouseDown.bind(this);
        this.handleEditorMouseUp = this.handleEditorMouseUp.bind(this);
    }

    private observeFormatBrushState = () => {
        this.reactionDisposers.push(reaction(() => {
            const formatBrushState = (this.getAppService() as IPcEditorAppServices)
                .editableServiceLayer?.formatBrushManager?.formatBrushState;
            return [formatBrushState];
        }, () => {
            this.updateTextEditorCursor();
        }));
    };

    // textEditor的层级不同，更新操作交给Inner自己来控制，这里提供cursor的样式
    private updateTextEditorCursor() {
        const cursor = this.getComputedCursor();
        this.component.inner.updateTextEditorCursor?.(cursor);
    }

    private handleUnHighlightSearchResult = () => {
        // 逻辑走到这里说明之前执行过 search，所以直接使用同步的 dangerousInstance 即可，如果 没执行过 search，后面的逻辑也不用走了
        const searchReplaceManager = (this.getAppService() as IPcEditorAppServices)
            .editableServiceLayer?.searchReplaceManager?.dangerousInstance;

        const currentComponentSearchResult = this.getCurrentComponentSearchResult();

        if (currentComponentSearchResult && searchReplaceManager) {
            const { keyword, lastHighlightPosition } = searchReplaceManager;
            const isCurrentHighlight = lastHighlightPosition?.component === this.factoryOptions.model.sdkData;

            this.renderSearchHighlight?.({
                // 搜索结果数据
                searchResult: currentComponentSearchResult.result,
                keyword,
                // 高亮结果数据
                highlightResult: isCurrentHighlight
                    ? currentComponentSearchResult.result[lastHighlightPosition.offset]
                    : undefined,
            });
        }
    };

    /*
     * 获取组件命中的搜索结果
     */
    private getCurrentComponentSearchResult = (): Result | undefined => {
        const searchReplaceManager = (this.getAppService() as IPcEditorAppServices)
            .editableServiceLayer?.searchReplaceManager?.dangerousInstance;

        if (searchReplaceManager) {
            const { currentPageResult, isSearchingFlag } = searchReplaceManager;
            if (currentPageResult && isSearchingFlag) {
                // 匹配出对应model是否在searchReplaceManage的搜索结果里
                return searchReplaceManager.currentPageResult
                    .find(searchResult => searchResult.component === this.factoryOptions.model.sdkData);
            }
        }

        return undefined;
    };

    // 子类需要根据实际业务实现这个方法，因为各自的textEditor层级不一样
    public abstract renderSearchHighlight (param: {
        searchResult: Array<SearchResultItem>;
        keyword: string;
        highlightResult: SearchResultItem | undefined;
    }): void;
}
